import React, { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { get } from "react-hook-form";
import {
  Box,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Link,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  ControlledInput,
  ControlledSelect,
  ControlledTextarea,
} from "@amzn/react-hook-form-polaris";
import { useGetTeamByIdQuery } from "pages/team_settings/endpoints";
import { InfoPopover } from "components/info-popover";
import { GAIA_TEAM_OPTIONS } from "utils/gaia-constants";
import { DISABLED_MESSAGES } from "utils/disabled-messages";
import {
  AssigneeTeamAndOwnerFieldsProps,
  RiskAndContextSectionProps,
  UseCrossTeamAssignmentResult,
} from "./definitions";

const NBSP_SENTINEL = " ";

export const useCrossTeamAssignment = (
  formMethods: UseFormReturn<any>,
): UseCrossTeamAssignmentResult => {
  const assigneeTeam = formMethods.watch("assigneeTeam");
  const assigneeTeamOptions = GAIA_TEAM_OPTIONS;

  const isAssigneeTeamSelected =
    !!assigneeTeam && assigneeTeam !== NBSP_SENTINEL;

  const { data: assigneeTeamData } = useGetTeamByIdQuery(
    { teamId: assigneeTeam, teamName: assigneeTeam },
    { skip: !isAssigneeTeamSelected },
  );

  const assigneeTeamPermissionsLink = React.useMemo(() => {
    const ldapGroupId = assigneeTeamData?.team?.ldapGroupId;
    if (!ldapGroupId) return "";
    const permissionsTeamName = ldapGroupId
      .replace("test-fintech-gaia-", "GaiaTest_")
      .replace("fintech-gaia-", "Gaia_");
    return `https://permissions.amazon.com/a/team/${permissionsTeamName}_Team`;
  }, [assigneeTeamData]);

  const prevAssigneeTeamRef = React.useRef(assigneeTeam);
  useEffect(() => {
    if (prevAssigneeTeamRef.current === assigneeTeam) return;
    prevAssigneeTeamRef.current = assigneeTeam;
    formMethods.setValue("assigneeOwner", undefined as any);
    if (!assigneeTeam) {
      formMethods.setValue("penaltyRiskLevel", null as any);
      formMethods.setValue("riskDetails", "");
      formMethods.setValue("dataSource", "");
    }
  }, [assigneeTeam, formMethods]);

  return {
    assigneeTeamOptions,
    assigneeTeam,
    isAssigneeTeamSelected,
    assigneeTeamPermissionsLink,
  };
};

export const AssigneeTeamAndOwnerFields: React.FC<
  AssigneeTeamAndOwnerFieldsProps
> = ({
  formMethods,
  readOnly,
  canEditAssigneeOwner,
  assigneeOwnerOptions: assigneeOwnerOptionsProp,
}) => {
  const {
    assigneeTeamOptions,
    isAssigneeTeamSelected,
    assigneeTeamPermissionsLink,
  } = useCrossTeamAssignment(formMethods);
  // Once an assignee owner has claimed the task, the assignee team is locked.
  const assigneeOwnerValue = formMethods.watch("assigneeOwner");
  const hasAssigneeOwner = !!assigneeOwnerValue;
  const assigneeOwnerDisabled = !canEditAssigneeOwner;
  // When editable, prepend a clear-sentinel option so users can unset the owner; when read-only with a value, synthesize a single option so the value stays visible.
  const assigneeOwnerOptions = canEditAssigneeOwner
    ? [
        { label: NBSP_SENTINEL, value: NBSP_SENTINEL },
        ...(assigneeOwnerOptionsProp ?? []),
      ]
    : assigneeOwnerValue
      ? [{ label: assigneeOwnerValue, value: assigneeOwnerValue }]
      : [];
  return (
    <ColumnLayout columns={2}>
      <FormField
        label={
          <Box fontWeight="bold">
            Assignee team - <i> optional</i>
          </Box>
        }
      >
        <SpaceBetween direction="vertical" size="xs">
          <ControlledSelect
            name="assigneeTeam"
            control={formMethods.control}
            options={assigneeTeamOptions}
            placeholder="Choose an option"
            filteringType="auto"
            disabled={
              readOnly ||
              hasAssigneeOwner ||
              formMethods.watch("status") === "Cancelled"
            }
            onChange={(e) => {
              if (e.detail.selectedOption.value === NBSP_SENTINEL) {
                formMethods.setValue("assigneeTeam", null as any);
              }
            }}
          />
          {formMethods.watch("status") === "Cancelled" && (
            <Box color="text-status-info" fontSize="body-s">
              {DISABLED_MESSAGES.assigneeTeam.cancelled}
            </Box>
          )}
          {formMethods.watch("status") !== "Cancelled" && readOnly && (
            <Box color="text-status-info" fontSize="body-s">
              {DISABLED_MESSAGES.assigneeTeam.ownerOnly}
            </Box>
          )}
          {formMethods.watch("status") !== "Cancelled" &&
            !readOnly &&
            hasAssigneeOwner && (
              <Box color="text-status-info" fontSize="body-s">
                {DISABLED_MESSAGES.assigneeTeam.ownerAssigned}
              </Box>
            )}
          {isAssigneeTeamSelected && assigneeTeamPermissionsLink && (
            <Link
              external={true}
              ariaLabel="Assignee team permissions link"
              target="_blank"
              href={assigneeTeamPermissionsLink}
            >
              View team members on permissions.amazon.com
            </Link>
          )}
        </SpaceBetween>
      </FormField>
      <FormField
        label={
          <Box fontWeight="bold">
            Assignee owner - <i> optional</i>
          </Box>
        }
      >
        <ControlledSelect
          name="assigneeOwner"
          control={formMethods.control}
          options={assigneeOwnerOptions}
          placeholder="Set by assignee team"
          disabled={assigneeOwnerDisabled}
          filteringType="auto"
          onChange={(e) => {
            if (e.detail.selectedOption.value === NBSP_SENTINEL) {
              formMethods.setValue("assigneeOwner", null as any);
            }
          }}
        />
        {assigneeOwnerDisabled && (
          <Box color="text-status-info" fontSize="body-s">
            {DISABLED_MESSAGES.assigneeOwner.wrongTeam}
          </Box>
        )}
      </FormField>
    </ColumnLayout>
  );
};

export const RiskAndContextSection: React.FC<RiskAndContextSectionProps> = ({
  formMethods,
  penaltyRiskLevelOptions,
}) => {
  const assigneeTeam = formMethods.watch("assigneeTeam");
  if (!assigneeTeam) return null;
  return (
    <Container header={<Header variant="h2">Risk & Context</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <ColumnLayout columns={2}>
          <FormField
            label="Penalty/risk level"
            errorText={get(
              formMethods.formState?.errors,
              "penaltyRiskLevel.message",
            )}
          >
            <ControlledSelect
              name="penaltyRiskLevel"
              control={formMethods.control}
              options={penaltyRiskLevelOptions}
              placeholder="Choose an option"
              filteringType="auto"
            />
          </FormField>
          <FormField
            label={
              <Box fontWeight="bold">
                Data source - <i> optional</i>
              </Box>
            }
          >
            <ControlledInput
              name="dataSource"
              control={formMethods.control}
              placeholder="e.g., SAP, Oracle, Braavos report, Seller Central"
            />
          </FormField>
        </ColumnLayout>
        <FormField
          label={
            <SpaceBetween direction="horizontal" size="xxs">
              <span>Risk/Priority details</span>
              <InfoPopover content="Describe the specific penalty, deadline, compliance impact, or reason for High priority. For Critical/High penalty risk, include the monetary amount and authority deadline if known." />
            </SpaceBetween>
          }
          errorText={get(formMethods.formState?.errors, "riskDetails.message")}
        >
          <ControlledTextarea
            name="riskDetails"
            control={formMethods.control}
            placeholder="Additional context on the specific penalty, deadline, or compliance impact."
          />
        </FormField>
      </SpaceBetween>
    </Container>
  );
};
